/*
 * Canvas-rendered placeholder generator for local mode when no Vertex
 * service-account JSON is uploaded yet. ~700ms artificial delay so the
 * UI's "generating…" shimmer is exercised. Per IMPLEMENTATION_PLAN §7.
 */

const W = 1280;
const H = 720;

export async function generateMockImage(prompt: string, sceneNumber: number): Promise<Blob> {
  await delay(700 + Math.random() * 200);
  const canvas = new OffscreenCanvas(W, H);
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Canvas 2D context unavailable');
  paintBackground(ctx);
  paintSceneNumber(ctx, sceneNumber);
  paintPrompt(ctx, prompt);
  paintMockBadge(ctx);
  return canvas.convertToBlob({ type: 'image/png' });
}

export async function generateMockVideoFrame(
  prompt: string,
  sceneNumber: number,
): Promise<Blob> {
  // Simulate slower video gen
  await delay(1100 + Math.random() * 300);
  const canvas = new OffscreenCanvas(W, H);
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Canvas 2D context unavailable');
  paintBackground(ctx);
  paintSceneNumber(ctx, sceneNumber);
  paintPrompt(ctx, prompt);
  paintVideoBadge(ctx);
  return canvas.convertToBlob({ type: 'image/png' });
}

function paintBackground(ctx: OffscreenCanvasRenderingContext2D): void {
  // Warm canvas color from tokens.css — kept in sync manually
  ctx.fillStyle = '#0d0b0a';
  ctx.fillRect(0, 0, W, H);
  // Subtle accent corner
  const grad = ctx.createRadialGradient(W * 0.85, H * 0.85, 0, W * 0.85, H * 0.85, W * 0.6);
  grad.addColorStop(0, 'rgba(245,179,74,0.18)');
  grad.addColorStop(1, 'rgba(245,179,74,0)');
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, W, H);
}

function paintSceneNumber(ctx: OffscreenCanvasRenderingContext2D, n: number): void {
  ctx.font = '600 220px ui-monospace, "Berkeley Mono", "JetBrains Mono", monospace';
  ctx.fillStyle = 'rgba(245,179,74,0.18)';
  ctx.textBaseline = 'top';
  ctx.fillText(String(n).padStart(2, '0'), 60, 60);
}

function paintPrompt(ctx: OffscreenCanvasRenderingContext2D, prompt: string): void {
  ctx.fillStyle = '#f5f0e6';
  ctx.font = '500 28px ui-sans-serif, system-ui, "Inter", sans-serif';
  ctx.textBaseline = 'top';
  const maxWidth = W - 120;
  const x = 60;
  let y = H - 200;
  const text = prompt.trim() || '(empty prompt)';
  const lines = wrap(ctx, text, maxWidth);
  for (const line of lines.slice(0, 4)) {
    ctx.fillText(line, x, y);
    y += 38;
  }
}

function paintMockBadge(ctx: OffscreenCanvasRenderingContext2D): void {
  paintBadge(ctx, 'MOCK · NO VERTEX KEY UPLOADED');
}

function paintVideoBadge(ctx: OffscreenCanvasRenderingContext2D): void {
  paintBadge(ctx, 'MOCK VIDEO · NO VERTEX KEY UPLOADED');
}

function paintBadge(ctx: OffscreenCanvasRenderingContext2D, text: string): void {
  ctx.font = '500 18px ui-monospace, monospace';
  const width = ctx.measureText(text).width + 24;
  const x = W - width - 32;
  const y = 32;
  ctx.fillStyle = 'rgba(245,179,74,0.2)';
  ctx.fillRect(x, y, width, 36);
  ctx.fillStyle = '#f5b34a';
  ctx.fillText(text, x + 12, y + 9);
}

function wrap(
  ctx: OffscreenCanvasRenderingContext2D,
  text: string,
  maxWidth: number,
): string[] {
  const words = text.split(/\s+/);
  const lines: string[] = [];
  let current = '';
  for (const word of words) {
    const candidate = current ? `${current} ${word}` : word;
    if (ctx.measureText(candidate).width > maxWidth && current) {
      lines.push(current);
      current = word;
    } else {
      current = candidate;
    }
  }
  if (current) lines.push(current);
  return lines;
}

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}
